// 📁 frontend/src/utils/biometric.js
// Wrapper biometric per APK Capacitor.
//
// Usa @aparajita/capacitor-biometric-auth (vs Capgo) perché supporta
// androidBiometryStrength: 'weak' che NON richiede biometric Class 3 (impronta HW)
// e quindi funziona con Face Recognition Samsung non-IR + fallback PIN.
//
// Storage credenziali: localStorage (le credenziali sono già lì come "easyjob_user"
// per il fallback offline, quindi non aggiungiamo esposizione).

const VAULT_FLAG_KEY = "easyjob_biometric_enrolled";
const VAULT_USER_KEY = "easyjob_biometric_username";
const VAULT_PASS_KEY = "easyjob_biometric_password";

function isCapacitor() {
	return typeof window !== "undefined" && !!window.Capacitor;
}

let pluginPromise = null;
async function getPlugin() {
	if (!isCapacitor()) return null;
	if (!pluginPromise) {
		pluginPromise = import("@aparajita/capacitor-biometric-auth")
			.then((mod) => mod)
			.catch((err) => {
				console.warn("[biometric] plugin load failed:", err);
				return null;
			});
	}
	return pluginPromise;
}

export async function isBiometricAvailable() {
	const m = await getPlugin();
	if (!m?.BiometricAuth) return false;
	try {
		const result = await m.BiometricAuth.checkBiometry();
		// Disponibile se c'è biometric o device credential (PIN/Pattern/Password)
		return Boolean(result?.isAvailable || result?.deviceIsSecure);
	} catch (err) {
		console.warn("[biometric] checkBiometry error:", err);
		return false;
	}
}

export async function getBiometricLabel() {
	const m = await getPlugin();
	if (!m?.BiometricAuth) return "biometrico";
	try {
		const result = await m.BiometricAuth.checkBiometry();
		const T = m.BiometryType || {};
		switch (result?.biometryType) {
			case T.faceId:
			case T.faceAuthentication:
			case 2:
			case 4:
				return "Face ID";
			case T.touchId:
			case T.fingerprintAuthentication:
			case 1:
			case 3:
				return "impronta";
			case T.irisAuthentication:
			case 5:
				return "iride";
			default:
				return "biometrico";
		}
	} catch {
		return "biometrico";
	}
}

export function hasBiometricVault() {
	if (typeof localStorage === "undefined") return false;
	return localStorage.getItem(VAULT_FLAG_KEY) === "1";
}

async function authenticateWithFallback(reason, title, subtitle) {
	const m = await getPlugin();
	if (!m?.BiometricAuth) throw new Error("Biometrico non disponibile");
	await m.BiometricAuth.authenticate({
		reason,
		cancelTitle: "Annulla",
		allowDeviceCredential: true,
		iosFallbackTitle: "Usa il codice",
		androidTitle: title,
		androidSubtitle: subtitle,
		androidConfirmationRequired: false,
		androidBiometryStrength: "weak",
	});
}

export async function enrollBiometric(username, password) {
	if (!username || !password) throw new Error("Credenziali mancanti per l'enrollment");
	await authenticateWithFallback(
		"Conferma la tua identità per attivare l'accesso rapido",
		"Attiva accesso rapido",
		"EasyJob — le credenziali resteranno sul dispositivo"
	);
	localStorage.setItem(VAULT_USER_KEY, username);
	localStorage.setItem(VAULT_PASS_KEY, password);
	localStorage.setItem(VAULT_FLAG_KEY, "1");
}

export async function unlockBiometric() {
	await authenticateWithFallback(
		"Sblocca EasyJob",
		"Accedi a EasyJob",
		"Usa il biometrico o il PIN del dispositivo"
	);
	const username = localStorage.getItem(VAULT_USER_KEY);
	const password = localStorage.getItem(VAULT_PASS_KEY);
	if (!username || !password) {
		throw new Error("Vault vuoto, riesegui il login con password");
	}
	return { username, password };
}

export async function clearBiometricVault() {
	localStorage.removeItem(VAULT_FLAG_KEY);
	localStorage.removeItem(VAULT_USER_KEY);
	localStorage.removeItem(VAULT_PASS_KEY);
}
